/**
 * skipOffline -- the pure rules behind "Skip offline switches" in the site VLAN rollout.
 *
 * Owner decision (2026-10-13, epic #1817): one offline switch still refuses the whole site
 * rollout BY DEFAULT. The operator may explicitly skip offline switches instead:
 *   * the plan lists each offline switch and what it would have received;
 *   * apply needs an acknowledgement naming EVERY skipped switch (one tick per switch);
 *   * the switch is flagged "pending VLAN changes" until a later run applies them, and the
 *     operator re-plans JUST that switch when it is back online (never applied automatically).
 *
 * No React, no fetch: unit-tested in skipOffline.test.ts (`npm run test:unit`); the components
 * in components/site/SiteVlanRollout.tsx only wire data in.
 */
import type {
  ApplyVars,
  PendingSwitch,
  PlanOptions,
  RolloutItem,
  SkippedOfflineSwitch,
  VlanRolloutPlan,
} from "./api/siteVlans";

/** The refusal code of an offline switch (services/site_vlan_rollout.REFUSAL_SWITCH_OFFLINE). */
export const OFFLINE_REFUSAL = "switch_unreachable";

/** A refusal the operator may waive by skipping offline switches. */
export function isSkippable(item: RolloutItem): boolean {
  return item.code === OFFLINE_REFUSAL && item.skippable === true;
}

/**
 * Should the page offer "Skip offline switches"? Only on a default (not yet skipping, not
 * scoped) plan that has at least one skippable refusal. Returns the switches it would skip and
 * whether skipping alone would unblock the plan (other refusals would still block it).
 */
export function skipOffer(plan: VlanRolloutPlan | null | undefined): {
  offer: boolean;
  switches: { serial: string; name: string }[];
  unblocks: boolean;
} {
  const none = { offer: false, switches: [], unblocks: false };
  if (!plan || plan.skip_offline || plan.scope) return none;
  const skippable = plan.refusals.filter(isSkippable);
  if (skippable.length === 0) return none;
  const serials = new Set(skippable.map((r) => r.serial).filter((s): s is string => !!s));
  const names = new Map((plan.offline_switches ?? []).map((s) => [s.serial, s.name]));
  // Refusals OTHER than the offline ones (an offline switch also reports e.g. "no state"; those
  // go away with it, so only refusals of devices that are not skipped count as still blocking).
  const blocking = plan.refusals.filter((r) => !isSkippable(r) && !(r.serial && serials.has(r.serial)));
  return {
    offer: true,
    switches: [...serials].sort().map((serial) => ({ serial, name: names.get(serial) ?? serial })),
    unblocks: blocking.length === 0,
  };
}

/** Has the operator ticked EVERY skipped switch (and nothing else)? */
export function ackComplete(plan: VlanRolloutPlan | null | undefined, acked: ReadonlySet<string>): boolean {
  const skipped = plan?.skipped_offline ?? [];
  if (skipped.length === 0) return true;
  if (acked.size !== skipped.length) return false;
  return skipped.every((s) => acked.has(s.serial));
}

/** "VLAN 400, 1000" (or "no VLANs") -- one list shape for every sentence below. */
export function vlanList(vlans: number[]): string {
  if (vlans.length === 0) return "no VLANs";
  return `VLAN ${vlans.join(", ")}`;
}

/** Plain-language line for one skipped switch: what it will NOT get. */
export function skippedLine(item: SkippedOfflineSwitch): string {
  const w = item.would_receive;
  const ports = w.trunk_ports.length
    ? ` on port${w.trunk_ports.length === 1 ? "" : "s"} ${w.trunk_ports.join(", ")}`
    : "";
  const removal = w.remove_vlans.length ? `; ${vlanList(w.remove_vlans)} would also be removed` : "";
  const basis = w.state_known ? "" : " (it never reported its ports, so the ports are not known)";
  return `${item.name} is offline and will not get ${vlanList(w.vlans)}${ports}${removal}${basis}.`;
}

/** The acknowledgement label next to one switch's tick box. */
export function ackLabel(item: SkippedOfflineSwitch): string {
  return `Skip ${item.name}: I understand it will not get ${vlanList(item.would_receive.vlans)} until it is re-planned.`;
}

/** The flag line on a switch with pending VLAN changes. */
export function pendingLine(p: PendingSwitch): string {
  const parts: string[] = [];
  if (p.pending_vlans.length) parts.push(`${vlanList(p.pending_vlans)} to add`);
  if (p.pending_removals.length) parts.push(`${vlanList(p.pending_removals)} to remove`);
  const what = parts.join("; ") || "VLAN changes";
  const state = p.online
    ? p.can_replan
      ? "It is back online: re-plan it to apply them."
      : "It is back online, but this switch cannot be configured by the rollout."
    : "It is still offline.";
  return `Pending VLAN changes on ${p.name}: ${what} (skipped while offline). ${state}`;
}

/** The read-back word for one device's verification status. */
export function verificationDeviceLabel(status: string): { tone: "healthy" | "warn" | "unknown"; label: string } {
  if (status === "verified") return { tone: "healthy", label: "verified" };
  if (status === "skipped_offline") return { tone: "unknown", label: "skipped (offline)" };
  if (status === "held_pending_replan") return { tone: "unknown", label: "held for re-plan" };
  return { tone: "warn", label: status.replace(/_/g, " ") };
}

/** Query params for a plan request; undefined for the default plan (shared cache entry). PURE. */
export function planParams(opts?: PlanOptions): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  if (opts?.switchSerial) params.switch = opts.switchSerial;
  else if (opts?.skipOffline) params.skip_offline = "true";
  return Object.keys(params).length ? params : undefined;
}

/** The apply body for ApplyVars. PURE (unit-tested). */
export function applyBody(vars: string | ApplyVars): Record<string, unknown> {
  const v: ApplyVars = typeof vars === "string" ? { planToken: vars } : vars;
  const body: Record<string, unknown> = { plan_token: v.planToken };
  if (v.switchSerial) body.switch = v.switchSerial;
  if (v.skipOffline) {
    body.skip_offline = true;
    body.acknowledge_skipped = [...(v.acknowledgeSkipped ?? [])];
  }
  return body;
}
